// `Model` from its own module, not the `@/models` barrel — see the note in `index.ts`.
import { Model } from './model';
import type { EntryUpdateReason } from './entry';
import { DateTime } from 'luxon';
import { LEGACY_REASON_MEANINGS, type SignatureMeaning, type SignatureType, type SignatureView } from './part11';

export interface ActivityResponse {
  activities: ActivityModel[],
  activityCount: number,
}

export interface NetworkActivity {
  activityOwner: {
    email: string;
    identifier: string;
    name: string;
  }
  createdAt: string;
  difference: {
    initialValues: NewValues
    newValues: NewValues
  }
  identifier: string;
  investigationIdentifier: string;
  organizationIdentifier: string;
  organizationOwner: {
    email: string;
    identifier: string;
    name: string;
  }
  /** The Part 11 signature on a row written since ENG-9104, on a Part 11 study. */
  signature?: SignatureView | null;
  subType: string;
  type: string;
  typeIdentifier: string;
  updatedAt: Date;
}

interface IActivity {
  activityOwner: {
    email: string;
    identifier: string;
    name: string;
  }
  difference: {
    newValues?: NewValues
  }
  signature?: SignatureView | null;
  createdAt: Date;
  subType: string;
  type: string;
  identifier: string;
}

interface NewValues {
  email: string;
  title: string;
  label: string;
  name: string;
  // Absent on a save that asked for no Part 11 reason.
  reasons?: EntryUpdateReason;
}

export class ActivityModel extends Model {
  activityOwner: {
    email: string;
    identifier: string;
    name: string;
  };
  createdAt: Date;
  difference: {
    initialValues?: NewValues
    newValues?: NewValues
  };
  signature?: SignatureView | null;
  subType: string;
  type: string;
  identifier: string;

  constructor({
    activityOwner,
    createdAt,
    difference,
    signature,
    subType,
    type,
    identifier
  }: IActivity) {
    super();
    this.activityOwner = activityOwner;
    this.createdAt = createdAt;
    this.difference = difference;
    this.signature = signature;
    this.subType = subType;
    this.type = type;
    this.identifier = identifier;
  }

  static mapFromBackend(networkActivity: NetworkActivity): ActivityModel {
    const simplifiedActivity: IActivity = {
      activityOwner: networkActivity.activityOwner,
      createdAt: DateTime.fromISO(networkActivity.createdAt).toJSDate(),
      difference: networkActivity.difference,
      signature: networkActivity.signature,
      subType: networkActivity.subType,
      type: networkActivity.type,
      identifier: networkActivity.identifier
    };
    return new ActivityModel(simplifiedActivity);
  }
}

/** A row's Part 11 signature as the history dialog draws it. */
export interface SignatureLine {
  /** The meaning as a code, for the reader's language. */
  meaning?: SignatureMeaning;
  /** An older row's sentence that matches none of the three meanings, drawn as it was stored. */
  sentence?: string;
  comment?: string;
  type?: SignatureType;
  /** The server checked no PIN a signed-in person sent for it: a wrong one, a locked one, or none. */
  unconfirmed: boolean;
}

/**
 * The typed `signature` a row written since ENG-9104 carries, or else the `reasons` an older row
 * holds, its English sentence read back to the meaning code (ENG-9100 decision 11). `undefined` for a
 * row with neither. A free function rather than a method: callers hand the dialog plain rows.
 */
export const signatureLineOf = (activity: Pick<ActivityModel, 'signature' | 'difference'>): SignatureLine | undefined => {
  const { signature } = activity;
  if (signature) {
    return {
      meaning: signature.meaning ?? undefined,
      comment: signature.comment ?? undefined,
      type: signature.type ?? undefined,
      // A public link's save is `none` too, and exempt: a respondent has no account, so no PIN to give.
      unconfirmed: signature.method === 'none' && !signature.viaPublicToken
    };
  }
  // Optional although typed as always there: the history dialog stopped throwing on a row without it (ENG-9108).
  const reasons = activity.difference?.newValues?.reasons;
  if (!reasons) {
    return undefined;
  }
  const meaning = Object.prototype.hasOwnProperty.call(LEGACY_REASON_MEANINGS, reasons.reason) ?
    LEGACY_REASON_MEANINGS[reasons.reason] :
    undefined;
  return {
    meaning,
    sentence: meaning ? undefined : reasons.reason || undefined,
    comment: reasons.comment || undefined,
    type: reasons.type,
    // No method was recorded before ENG-9104, so an older row draws as it always has.
    unconfirmed: false
  };
};
